import React, { useEffect, useMemo, useState } from "react";
import { Crown, ArrowsClockwise } from "@phosphor-icons/react";
import { PlayerHeadshot, useHeadshotMap } from "@/components/TeamVisuals";
import { teamTextColor } from "@/lib/teams";
import { Slider } from "@/components/ui/slider";

// Sprint 76/77 — Showdown crown spread: one bar per captain with portfolio share,
// ceiling and ownership, plus a max-share slider + per-captain caps that rebuild.
export default function CaptainCeilingChart({ lineups, capPct, capSource, overrides = {}, onOverridesChange, running = false }) {
  const headshots = useHeadshotMap();
  const tierCap = Number(capPct) > 0 ? Number(capPct) : 20;
  const [cap, setCap] = useState(Math.round(tierCap));
  useEffect(() => { setCap(Math.round(tierCap)); }, [tierCap]);
  const rows = useMemo(() => {
    const byId = new Map();
    (lineups || []).forEach((L) => {
      const c = (L.players || []).find((p) => String(p.roster_slot || "").toUpperCase() === "CPT");
      if (!c) return;
      const k = String(c.dk_id);
      const e = byId.get(k) || { ...c, count: 0 };
      e.count += 1;
      byId.set(k, e);
    });
    return [...byId.values()].sort((a, b) => b.count - a.count);
  }, [lineups]);
  const n = (lineups || []).length;
  if (!n || !rows.length) return null;
  const cptCeil = (r) => 1.5 * Number(r.ceiling || r.projection || 0);
  const maxCeil = Math.max(...rows.map(cptCeil), 1);
  const maxShare = Math.max(...rows.map((r) => r.count / n), 0.01);
  const dirty = cap !== Math.round(tierCap);
  const rebuild = () => {
    window.dispatchEvent(new CustomEvent("wp:rebuild-showdown", { detail: { cpt_max_share_pct: cap } }));
  };
  const setPlayerCap = (dkId, v) => {
    if (typeof onOverridesChange !== "function") return;
    onOverridesChange((prev) => {
      const cur = { ...(prev?.[dkId] || {}) };
      if (v === "" || v === null || Number.isNaN(Number(v))) delete cur.cpt_max_exposure_pct;
      else cur.cpt_max_exposure_pct = Math.max(0, Math.min(100, Number(v)));
      const next = { ...(prev || {}) };
      if (Object.keys(cur).length) next[dkId] = cur; else delete next[dkId];
      return next;
    });
  };
  return (
    <div className="border border-zinc-800 bg-[#0a0a0a]" data-testid="captain-ceiling-chart">
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
        <div className="overline flex items-center gap-2"><Crown size={12} weight="fill" className="text-[#FFCC00]" /> Crown Spread · {rows.length} captains</div>
        <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">share · cpt ceiling · own · max</div>
      </div>
      <div className="px-4 py-3 border-b border-zinc-800" data-testid="captain-cap-controls">
        <div className="flex items-center justify-between mb-2">
          <span className="mono text-[9px] uppercase tracking-widest text-zinc-500">Max share per captain</span>
          <span className="mono text-[11px] font-black text-[#FFCC00]" data-testid="captain-cap-value">{cap}%<span className="text-zinc-500 font-normal"> · ≤{Math.floor((cap / 100) * n)} of {n}</span></span>
        </div>
        <div className="flex items-center gap-3">
          <Slider data-testid="captain-cap-slider" value={[cap]} min={5} max={50} step={1} onValueChange={(v) => setCap(v[0])} className="flex-1" />
          <button
            type="button"
            data-testid="captain-cap-rebuild"
            onClick={rebuild}
            disabled={running}
            className={`mono text-[9px] uppercase tracking-widest px-3 py-1.5 border flex items-center gap-1.5 transition-colors ${dirty ? "border-[#FFCC00] text-[#FFCC00] hover:bg-[#FFCC00] hover:text-black" : "border-zinc-700 text-zinc-300 hover:border-zinc-500"} disabled:opacity-40`}
          >
            <ArrowsClockwise size={11} className={running ? "animate-spin" : ""} /> Rebuild
          </button>
        </div>
        <div className="mono text-[8px] text-zinc-500 mt-1.5">
          {capSource === "user" ? "Custom cap active" : "Contest-tier cap"} · slide, then Rebuild (~5 s). Per-captain max in the last column overrides the slider for that player.
        </div>
      </div>
      <div className="px-4 py-3 space-y-2">
        {rows.map((r) => {
          const share = r.count / n;
          const ceil = cptCeil(r);
          const own = Number(r.market_mean_own || r.our_own || 0);
          const hot = share > cap / 100 + 1e-9;
          const pcap = overrides?.[String(r.dk_id)]?.cpt_max_exposure_pct;
          return (
            <div key={r.dk_id} className="flex items-center gap-2" data-testid={`captain-row-${r.dk_id}`}>
              <PlayerHeadshot player={r} sleeperId={headshots[String(r.dk_id)]} size={22} />
              <div className="w-[104px] min-w-0">
                <div className="mono text-[10px] text-white font-bold truncate">{r.name}</div>
                <div className="mono text-[8px] text-zinc-500">{r.position} · <span className="font-black" style={{ color: teamTextColor(r.team) }}>{r.team}</span></div>
              </div>
              <div className="flex-1 space-y-[3px]">
                <div className="h-[7px] bg-zinc-800 relative" title={`${r.count} of ${n} lineups`}>
                  <div className={`h-full ${hot ? "bg-[#FF8A00]" : "bg-[#39FF14]"}`} style={{ width: `${Math.max(2, (share / maxShare) * 100)}%` }} />
                  <div className="absolute top-[-2px] bottom-[-2px] w-px bg-[#FFCC00]" style={{ left: `${Math.min(100, ((cap / 100) / maxShare) * 100)}%` }} title={`cap ${cap}%`} />
                </div>
                <div className="h-[4px] bg-zinc-800" title={`CPT ceiling ${ceil.toFixed(1)} pts (1.5×)`}>
                  <div className="h-full bg-zinc-500" style={{ width: `${Math.max(1, (ceil / maxCeil) * 100)}%` }} />
                </div>
              </div>
              <div className="text-right w-[36px] mono text-[11px] font-black" style={{ color: hot ? "#FF8A00" : "#39FF14" }} data-testid={`captain-share-${r.dk_id}`}>{(share * 100).toFixed(0)}%</div>
              <div className="text-right w-[32px] mono text-[9px] text-zinc-300" title="CPT ceiling (1.5× pts)">{ceil.toFixed(1)}</div>
              <div className={`text-right w-[30px] mono text-[9px] ${own >= 25 ? "text-[#FF3B30]" : own <= 10 ? "text-[#39FF14]" : "text-zinc-400"}`} title="projected ownership">{own.toFixed(0)}%</div>
              <input
                type="number" min={0} max={100} step={1}
                value={pcap ?? ""}
                placeholder={String(cap)}
                onChange={(e) => setPlayerCap(String(r.dk_id), e.target.value)}
                data-testid={`captain-max-input-${r.dk_id}`}
                title="Max share for this captain (%) — overrides the slider"
                className={`w-[42px] bg-transparent border px-1 py-0.5 mono text-[9px] text-right focus:outline-none ${pcap != null ? "border-[#FFCC00] text-[#FFCC00]" : "border-zinc-700 text-zinc-400"}`}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
